import { createContext, useContext, useEffect, ReactNode, useCallback, useMemo, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { LanguageCode } from '../types/language';
import { LANGUAGES } from '../utils/languageData';
import { t as translateFn, tr as translateText } from '../utils/i18n';
import { LOCALE_META, SiteLocale, localePath, splitLocale } from '../i18n/siteLocales';
import { setCurrentLanguage } from '../i18n/text';

interface LanguageContextType {
  // Current language — always taken from the URL (/hi/…, /ar/…, /es/…; no prefix = English)
  language: LanguageCode;
  /** Path of the current page without the language prefix ("/pricing"). */
  basePath: string;

  languageName: string;
  nativeName: string;
  flag: string;
  isRTL: boolean;

  /** Go to the current page in another language (or that language's home when the page isn't translated). */
  setLanguage: (language: LanguageCode) => void;
  /** Path of an internal link in the current language. */
  localize: (path: string) => string;

  t: (key: string, params?: Record<string, any>) => any;
  /** Inline English string in the visitor's language ({{name}} placeholders allowed). */
  tr: (text: string, params?: Record<string, any>) => string;

  isLoading: boolean;
  isInitialized: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'trainzilla_language_preference';

// Runs in the browser and in the prerender; useLayoutEffect so the HTML snapshot has lang/dir set.
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, path: basePath } = splitLocale(location.pathname);
  const language = locale as LanguageCode;
  // Before children render: module-level tr() (src/i18n/text.ts) reads this.
  setCurrentLanguage(language);

  useIsoLayoutEffect(() => {
    document.documentElement.dir = LOCALE_META[locale].rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
  }, [locale]);

  const setLanguage = useCallback(
    (next: LanguageCode) => {
      const target = next as SiteLocale;
      try {
        localStorage.setItem(STORAGE_KEY, target);
      } catch {
        /* private mode */
      }
      const translated = localePath(basePath, target);
      // Page isn't translated: go to that language's home rather than staying in English.
      const dest = target !== 'en' && translated === basePath ? localePath('/', target) : translated;
      navigate(dest + location.search + location.hash);
    },
    [basePath, location.search, location.hash, navigate],
  );

  const localize = useCallback((path: string) => localePath(path, locale), [locale]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => translateFn(key, language, params),
    [language],
  );

  const tr = useCallback(
    (text: string, params?: Record<string, string | number>) => translateText(text, language, params),
    [language],
  );

  const value = useMemo<LanguageContextType>(() => {
    const meta = LANGUAGES[language];
    return {
      language,
      basePath,
      languageName: meta.name,
      nativeName: meta.nativeName,
      flag: meta.flag,
      isRTL: LOCALE_META[locale].rtl,
      setLanguage,
      localize,
      t,
      tr,
      isLoading: false,
      isInitialized: true,
    };
  }, [language, basePath, locale, setLanguage, localize, t, tr]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
